import React, { FC } from 'react';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslate } from '@refinedev/core';

import { ICategory } from '../../../../interfaces';
import { Button, Col, Divider, Form, Row } from 'antd';
import { useSelect } from '@refinedev/antd';
import { DatePicker, FormItem, Input, Select, SelectProps } from '../../../ui';
import cn from 'classnames';
import styles from './certificatesInCountries.module.scss';

export const CertificatesInCountries: FC = () => {
    const t = useTranslate();

    const { selectProps: countriesProps } = useSelect<ICategory>({
        resource: 'dictionary/countries',
        defaultValue: [],
        optionLabel: 'name',

        onSearch: value => [
            {
                field: 'query',
                operator: 'contains',
                value,
            },
        ],

        meta: {},

        pagination: {
            mode: 'server',
        },
    }) as {
        selectProps: SelectProps<number>;
    };

    return (
        <Row gutter={[16, 16]}>
            <Col
                xs={24}
                md={24}
                className={cn(styles.col, 'certificates_in_countries')}
            >
                <Form.List name="certificates_in_countries">
                    {(fields, { add, remove }, { errors }) => (
                        <div>
                            {fields?.length > 0 && (
                                <Divider>
                                    {t(
                                        'applications.fields.certificates_in_countries'
                                    )}
                                </Divider>
                            )}
                            {fields.map((field, index) => {
                                return (
                                    <div
                                        key={field.key}
                                        className={styles.holder}
                                    >
                                        <Row gutter={[16, 16]}>
                                            <Col span={24}>
                                                <FormItem
                                                    {...field}
                                                    name={[
                                                        field.name,
                                                        'countryId',
                                                    ]}
                                                >
                                                    <Select
                                                        {...countriesProps}
                                                        label={t(
                                                            'applications.fields.country'
                                                        )}
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                    />
                                                </FormItem>
                                            </Col>

                                            <Col span={24}>
                                                <FormItem
                                                    {...field}
                                                    name={[
                                                        field.name,
                                                        'certificateNum',
                                                    ]}
                                                >
                                                    <Input
                                                        label={t(
                                                            'applications.fields.certificate_num'
                                                        )}
                                                    />
                                                </FormItem>
                                            </Col>

                                            <Col span={24}>
                                                <FormItem
                                                    {...field}
                                                    name={[
                                                        field.name,
                                                        'certificateDate',
                                                    ]}
                                                    getValueProps={value => {
                                                        if (!value) {
                                                            return {
                                                                value: undefined,
                                                            };
                                                        }

                                                        if (
                                                            dayjs.isDayjs(value)
                                                        ) {
                                                            return {
                                                                value,
                                                            };
                                                        }

                                                        return {
                                                            value: dayjs(value),
                                                        };
                                                    }}
                                                    getValueFromEvent={(
                                                        value: Dayjs | null
                                                    ) =>
                                                        value
                                                            ? value.format(
                                                                  'YYYY-MM-DD'
                                                              )
                                                            : undefined
                                                    }
                                                >
                                                    <DatePicker
                                                        label={t(
                                                            'applications.fields.certificate_date'
                                                        )}
                                                        placeholder={t(
                                                            'applications.fields.select_date'
                                                        )}
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                    />
                                                </FormItem>
                                            </Col>

                                            <Col span={24}>
                                                <Row
                                                    justify="end"
                                                    gutter={[16, 16]}
                                                >
                                                    <Col>
                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={
                                                                <MinusCircleOutlined />
                                                            }
                                                            onClick={() =>
                                                                remove(
                                                                    field.name
                                                                )
                                                            }
                                                            aria-label={t(
                                                                'buttons.delete'
                                                            )}
                                                        >
                                                            {t(
                                                                'buttons.delete'
                                                            )}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            })}
                            {fields?.length > 0 && (
                                <Divider
                                    style={{
                                        margin: '16px 0',
                                    }}
                                />
                            )}
                            <Button
                                type="dashed"
                                onClick={() => {
                                    add();
                                }}
                                block
                                style={{ marginBottom: 20 }}
                            >
                                <PlusOutlined />{' '}
                                {t('applications.fields.add_country')}
                            </Button>
                            <Form.ErrorList errors={errors} />
                        </div>
                    )}
                </Form.List>
            </Col>
        </Row>
    );
};
